// Card frame variants — visual differentiation for tier-6 mythic cards and
// the California foil cards.
//
// Returns Tailwind classes + inline style overrides that CardTile applies
// on top of its base styling. Keeping these as pure functions (not wrapper
// components) avoids a deeper component tree for a styling-only concern.

import type { Card } from '../../game/data/cards/types';

export interface FrameVariant {
  /** Classname appended to CardTile's outer container. */
  containerClass: string;
  /** Inline border color override. Falls back to CardTile's default when omitted. */
  borderColor?: string;
  /** Inline box-shadow override (used for halo / glow effects). */
  boxShadow?: string;
  /** Background style override for the portrait area. */
  portraitBackground?: string;
  /** Whether the tribe-accessory layer should render on this frame. */
  showTribeAccessory: boolean;
}

/**
 * Resolve which frame variant applies for a given card. Priority:
 * 1. Foil (California foils) — strongest visual differentiator (outgroup)
 * 2. Mythic (tier 6) — gold inlay + backlight
 * 3. Default — no overrides
 */
export function resolveFrameVariant(card: Card): FrameVariant {
  if (card.tags?.includes('foil')) return foilFrame();
  if (card.tier === 6) return mythicFrame();
  return defaultFrame();
}

function defaultFrame(): FrameVariant {
  return {
    containerClass: '',
    showTribeAccessory: true,
  };
}

/**
 * Tier-6 mythic frame. Gold inlay + halo backlight + outer glow shadow.
 * Visually unmistakable as "this is a legendary figure" before stats are read.
 */
function mythicFrame(): FrameVariant {
  return {
    containerClass: 'mythic-frame',
    borderColor: '#d4a945', // tecate gold
    boxShadow:
      '0 0 0 1px rgba(212,169,69,0.6), 0 0 16px rgba(212,169,69,0.4), 0 0 32px rgba(212,169,69,0.2)',
    portraitBackground:
      'radial-gradient(circle at 50% 35%, rgba(212,169,69,0.18) 0%, rgba(255,138,61,0.05) 40%, #130f0a 85%)',
    showTribeAccessory: true,
  };
}

/**
 * California foil frame. The outgroup gets a distinctly cleaner look —
 * white border, no radial vignette, no tribe-accessory clutter.
 * Reads as "this character doesn't quite belong here" at a glance.
 */
function foilFrame(): FrameVariant {
  return {
    containerClass: 'foil-frame',
    borderColor: 'rgba(245,236,214,0.5)', // bone (clean, slightly off-white)
    boxShadow: '0 0 0 1px rgba(245,236,214,0.15)',
    portraitBackground:
      'linear-gradient(180deg, rgba(245,236,214,0.04) 0%, #130f0a 70%)',
    // Foils get their own visual register — no tribe accessory.
    // They use their own brand iconography (e.g., ring light for influencer).
    showTribeAccessory: false,
  };
}
